import { Button, Empty, Flex, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UploadedFileCarousel from '@/components/general/UploadFile/UploadedFileCarousel';
import UploadedFileList from '@/components/general/UploadFile/UploadedFileList';
import UploadFileComponent from '@/components/general/UploadFile/UploadFile';
import { useUserContext } from '@/contexts/UserContext';
import FileData from '@/model/General/FileData';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import { getShippingLabelFileCode, getTaxFileCode } from './shipmentTaxFileUtils';

interface ShipmentShowFilesProps {
  record?: Shipment;
  canEdit?: boolean;
}

/**
 * Estados en los que se permite cargar fotos generales del envío: desde Preparado
 * en adelante, excluyendo Finalizado y los estados de excepción (Cancelado, Reparación,
 * Gestión administrativa, L.I. Completada).
 */
const UPLOAD_ALLOWED_STATES = [
  PACKAGE_STATES.PREPARADO,
  PACKAGE_STATES.EN_VIAJE,
  PACKAGE_STATES.RECIBIDO,
];
const ShipmentShowFiles = (props: ShipmentShowFilesProps) => {
  const { t } = useTranslation();
  const { userWorkArea } = useUserContext();
  const { record, canEdit } = props;
  const [packageFiles, setPackageFiles] = useState<FileData[]>([]);
  const [uploadFiles, setUploadFiles] = useState<FileData[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const { packageRepository } = useRepositoriesContext();

  useEffect(() => {
    if (record?.photos && record?.id) {
      const taxCode = getTaxFileCode(record.id);
      const labelCode = getShippingLabelFileCode(record.id);
      // El archivo tributario y la etiqueta de envío se gestionan en sus propias secciones
      const fileList = record.photos
        .filter(fileData => fileData.description !== taxCode && fileData.description !== labelCode)
        .map(
          fileData =>
            new FileData({
              id: fileData.id,
              title: fileData.description,
              file: fileData.photo,
            }),
        );

      setPackageFiles(fileList);
    }
  }, [record?.photos, record?.id]);

  const canUpload =
    userWorkArea === USER_WORK_AREA.LOGISTICS &&
    record?.status !== undefined &&
    UPLOAD_ALLOWED_STATES.includes(record.status);

  return (
    <Flex vertical gap={16} align="flex-end">
      {packageFiles.length > 0 ? (
        <UploadedFileCarousel
          fileList={packageFiles}
          setFileList={setPackageFiles}
          canRemove={canEdit}
        />
      ) : (
        <Empty style={{ width: '100%' }} description={t('No hay fotos cargadas')} />
      )}
      {canUpload && (
        <>
          <Button
            style={{ width: 'fit-content', margin: '0 24px' }}
            type="primary"
            onClick={() => setOpenModal(true)}
            disabled={!canEdit}>
            {t('Cargar archivos')}
          </Button>

          <Modal
            title={t('Cargar archivos')}
            open={openModal}
            onCancel={() => {
              setOpenModal(false);
              setUploadFiles([]);
            }}
            okText={t('Cargar archivos')}
            cancelText={t('Cancelar')}
            onOk={() => {
              Modal.confirm({
                title: t('¿Estás seguro de que deseas cargar estos archivos?'),
                content: t('Esta acción no se puede deshacer'),
                okText: t('Sí'),
                okType: 'primary',
                cancelText: t('No'),
                onOk: () => {
                  if (!record?.id) {
                    setOpenModal(false);
                    setUploadFiles([]);

                    return;
                  }

                  void packageRepository.saveFiles(uploadFiles, record.id).then(() => {
                    setPackageFiles(prev => [...prev, ...uploadFiles]);
                    setUploadFiles([]);
                    setOpenModal(false);
                  });
                },
              });
            }}>
            <UploadFileComponent fileList={uploadFiles} setFileList={setUploadFiles} />
            {uploadFiles.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <UploadedFileList fileList={uploadFiles} setFileList={setUploadFiles} />
              </div>
            )}
          </Modal>
        </>
      )}
    </Flex>
  );
};

export default ShipmentShowFiles;
